import React, { useEffect, useState } from "react";
import {
  Table,
  Button,
  Badge,
  Form,
  Spinner,
  Card,
  InputGroup,
  Modal,
  Row,
  Col,
  Pagination,
} from "react-bootstrap";
import {
  Search,
  Funnel,
  Eye,
  Reply,
  ArrowClockwise,
  Calendar,
  Person,
  Briefcase,
  Chat,
  CheckCircle,
  XCircle,
  Envelope,
  GeoAlt,
} from "react-bootstrap-icons";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const API_BASE = "https://api.ownerbuilderssoftware.com/api";

/* ================= TICKET STATUS MAP ================= */
const TICKET_STATUS_MAP = {
  open: { label: "Open", variant: "warning", color: "#ffc107" },
  closed: { label: "Closed", variant: "success", color: "#198754" },
  pending: { label: "Pending", variant: "info", color: "#0dcaf0" },
};

const pad2 = (value) => String(value).padStart(2, "0");

const getTicketReceivedAt = (ticket) =>
  ticket?.createdAt || ticket?.lastMessageAt || ticket?.updatedAt || null;

const formatTicketCode = (dateString) => {
  if (!dateString) return "TKTUNKNOWN";

  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return "TKTUNKNOWN";

  return `TKT${date.getFullYear()}${pad2(date.getMonth() + 1)}${pad2(
    date.getDate()
  )}${pad2(date.getHours())}${pad2(date.getMinutes())}`;
};

const HelpAndSupport = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);

  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("");

  const [selectedTicket, setSelectedTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(false);

  const [showView, setShowView] = useState(false);
  const [replyMessage, setReplyMessage] = useState("");
  const [replying, setReplying] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [messagesPage, setMessagesPage] = useState(1);
  const [hasMoreMessages, setHasMoreMessages] = useState(false);
  const limit = 10;

  const token =
    localStorage.getItem("adminToken") || localStorage.getItem("token");

  /* ================= FETCH TICKETS ================= */
  const fetchTickets = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_BASE}/support/tickets`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          page: currentPage,
          limit,
          search: searchTerm || undefined,
          status: filterStatus || undefined,
        },
      });

      if (res.data.success) {
        const sortedTickets = [...(res.data.tickets || [])].sort((a, b) => {
          const aTime = new Date(getTicketReceivedAt(a) || 0).getTime();
          const bTime = new Date(getTicketReceivedAt(b) || 0).getTime();
          return bTime - aTime;
        });

        setTickets(sortedTickets);
        setTotalPages(Math.ceil((res.data.count || 0) / limit));
        setTotalRecords(res.data.count || 0);
      }
    } catch (error) {
      console.error("Fetch tickets error:", error);
      toast.error("Failed to fetch tickets");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTickets();
  }, [currentPage, searchTerm, filterStatus]);

  /* ================= FETCH TICKET MESSAGES ================= */
  const fetchTicketMessages = async (ticketId, page = 1) => {
    try {
      setMessagesLoading(true);

      const res = await axios.get(`${API_BASE}/support/messages/${ticketId}`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { page, limit: 10 }, // 👈 10 messages per load
      });

      if (res.data.success) {
        if (page === 1) {
          // first load → latest messages
          setMessages(res.data.messages || []);
        } else {
          // prepend older messages at top
          setMessages((prev) => [...res.data.messages, ...prev]);
        }

        setHasMoreMessages(res.data.hasMore || false);
        setMessagesPage(page);
      }
    } catch {
      toast.error("Failed to load messages");
    } finally {
      setMessagesLoading(false);
    }
  };

  /* ================= VIEW TICKET DETAILS ================= */
  const handleViewTicket = async (ticket) => {
    setSelectedTicket(ticket);
    setShowView(true);
    setReplyMessage("");
    setMessages([]);
    setMessagesPage(1);
    setHasMoreMessages(false);

    await fetchTicketMessages(ticket._id, 1);
  };

  /* ================= SEND REPLY ================= */
  const handleSendReply = async (e) => {
    e.preventDefault();
    if (!replyMessage.trim()) {
      toast.warning("Please enter a message");
      return;
    }

    try {
      setReplying(true);
      const res = await axios.post(
        `${API_BASE}/support/adminSendMessage`,
        {
          ticketId: selectedTicket._id,
          message: replyMessage.trim(),
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (res.data.success) {
        toast.success("Message sent successfully");
        setReplyMessage("");
        // Refresh messages
        await fetchTicketMessages(selectedTicket._id, 1);
        // Refresh tickets list to update lastMessageAt
        fetchTickets();
      }
    } catch (error) {
      console.error("Send reply error:", error);
      toast.error("Failed to send message");
    } finally {
      setReplying(false);
    }
  };

  /* ================= CLOSE TICKET ================= */
  const handleCloseTicket = async (ticketId) => {
    if (!window.confirm("Are you sure you want to close this ticket?")) return;

    try {
      await axios.put(
        `${API_BASE}/support/close/${ticketId}`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      toast.success("Ticket closed successfully");
      fetchTickets();
      if (selectedTicket?._id === ticketId) {
        setShowView(false);
      }
    } catch (error) {
      console.error("Close ticket error:", error);
      toast.error("Failed to close ticket");
    }
  };

  /* ================= REOPEN TICKET ================= */
  const handleReopenTicket = async (ticketId) => {
    try {
      await axios.put(
        `${API_BASE}/support/reopen/${ticketId}`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      toast.success("Ticket reopened successfully");
      fetchTickets();
    } catch (error) {
      console.error("Reopen ticket error:", error);
      toast.error("Failed to reopen ticket");
    }
  };

  /* ================= HELPERS ================= */
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);

    if (diffInSeconds < 60) return "Just now";
    if (diffInSeconds < 3600)
      return `${Math.floor(diffInSeconds / 60)} minutes ago`;
    if (diffInSeconds < 86400)
      return `${Math.floor(diffInSeconds / 3600)} hours ago`;
    if (diffInSeconds < 604800)
      return `${Math.floor(diffInSeconds / 86400)} days ago`;
    return formatDate(dateString);
  };

  const renderPagination = () => {
    const items = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    // First page
    if (startPage > 1) {
      items.push(
        <Pagination.Item key={1} onClick={() => setCurrentPage(1)}>
          1
        </Pagination.Item>
      );
      if (startPage > 2) {
        items.push(<Pagination.Ellipsis key="ellipsis-start" />);
      }
    }

    // Page numbers
    for (let i = startPage; i <= endPage; i++) {
      items.push(
        <Pagination.Item
          key={i}
          active={i === currentPage}
          onClick={() => setCurrentPage(i)}
        >
          {i}
        </Pagination.Item>
      );
    }

    // Last page
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        items.push(<Pagination.Ellipsis key="ellipsis-end" />);
      }
      items.push(
        <Pagination.Item
          key={totalPages}
          onClick={() => setCurrentPage(totalPages)}
        >
          {totalPages}
        </Pagination.Item>
      );
    }

    return items;
  };

  /* ================= RENDER MESSAGE BUBBLE ================= */
  const renderMessageBubble = (message) => {
    const isAdmin = message.senderRole === "admin";
    return (
      <div
        className={`d-flex mb-3 ${
          isAdmin ? "justify-content-end" : "justify-content-start"
        }`}
      >
        <div
          className={`p-3 rounded-3 ${
            isAdmin ? "text-white chat-reply-bg" : "bg-light"
          }`}
          style={{ maxWidth: "70%" }}
        >
          <div className="d-flex align-items-center mb-1">
            <small
              className={`fw-bold ${isAdmin ? "text-white-70" : "text-muted"}`}
            >
              {isAdmin ? "You" : selectedTicket?.user?.name}
            </small>
            <small
              className={`ms-2 ${isAdmin ? "text-white-70" : "text-muted"}`}
            >
              {formatDate(message.createdAt)}
            </small>
          </div>
          <div>{message.message}</div>
        </div>
      </div>
    );
  };

  return (
    <div style={{ backgroundColor: "#ffffffff", minHeight: "100vh" }}>
      <ToastContainer position="top-right" autoClose={3000} />

      {/* HEADER + FILTERS */}
      <Card className="border-0 mb-4">
        <Card.Body className="p-0">
          <Row className="align-items-center g-3 justify-content-between">
            {/* TITLE */}
            <Col md={4}>
              <h4 className="fw-bold mb-0">Help & Support</h4>
              <small className="text-muted">
                Manage user support tickets in received order ({totalRecords} total)
              </small>
            </Col>

            {/* REFRESH */}
            <Col md={1} className="text-end">
              <Button
                variant="outline-primary"
                onClick={fetchTickets}
                title="Refresh"
              >
                <ArrowClockwise />
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* TABLE */}
      <Card className="border-0 shadow-sm">
        <Card.Body className="p-0">
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" />
              <p className="mt-2 text-muted">Loading tickets...</p>
            </div>
          ) : tickets.length === 0 ? (
            <div className="text-center py-5">
              <Chat size={48} className="text-muted mb-3" />
              <h5>No tickets found</h5>
              <p className="text-muted">
                {searchTerm || filterStatus
                  ? "Try changing your search or filter criteria"
                  : "No support tickets have been created yet"}
              </p>
            </div>
          ) : (
            <>
              <div className="table-responsive">
                <Table hover className="mb-0">
                  <thead className="table-dark">
                    <tr>
                      <th>Ticket Code</th>
                      <th>User</th>
                      <th>Project</th>
                      <th>Subject</th>
                      <th>Last Activity</th>
                      <th>Received At</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tickets.map((ticket) => (
                      <tr key={ticket._id}>
                        <td>
                          <small className="text-muted">
                            {formatTicketCode(getTicketReceivedAt(ticket))}
                          </small>
                        </td>
                        <td>
                          <div className="d-flex align-items-center">
                            <div>
                              <div className="fw-medium">
                                <Person className="me-1" />
                                {ticket?.name}
                              </div>
                              <small className="text-muted">
                                <Envelope className="me-1" size={12} />
                                {ticket?.email || "No email"}
                              </small>
                            </div>
                          </div>
                        </td>
                        <td>
                          {ticket.project ? (
                            <div>
                              <Briefcase className="me-2" />
                              {ticket.project.name}
                              <br />
                              <small className="text-muted">
                                <GeoAlt className="me-1" size={12} />
                                {ticket.project.projectLocation}
                              </small>
                            </div>
                          ) : (
                            <span className="text-muted">No project</span>
                          )}
                        </td>
                        <td>
                          <div className="fw-medium">{ticket.subject}</div>
                        </td>
                        <td>
                          <small>{getTimeAgo(ticket.lastMessageAt)}</small>
                        </td>
                        <td>
                          <small>{formatDate(getTicketReceivedAt(ticket))}</small>
                        </td>
                        <td>
                          <div className="d-flex gap-2">
                            <Button
                              size="sm"
                              variant="primary"
                              onClick={() => handleViewTicket(ticket)}
                              title="View & Reply"
                            >
                              <Eye />
                            </Button>
                            {/* {ticket.status === "closed" ? (
                              <Button
                                size="sm"
                                variant="success"
                                onClick={() => handleReopenTicket(ticket._id)}
                                title="Reopen Ticket"
                              >
                                <CheckCircle />
                              </Button>
                            ) : (
                              <Button
                                size="sm"
                                variant="danger"
                                onClick={() => handleCloseTicket(ticket._id)}
                                title="Close Ticket"
                              >
                                <XCircle />
                              </Button>
                            )} */}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>

              {/* PAGINATION */}
              <div className="p-3 d-flex justify-content-between align-items-center">
                <small className="text-muted">
                  Showing {(currentPage - 1) * limit + 1}–
                  {Math.min(currentPage * limit, totalRecords)} of{" "}
                  {totalRecords} tickets
                </small>
                {totalPages > 1 && (
                  <Pagination className="mb-0">
                    <Pagination.Prev
                      onClick={() =>
                        setCurrentPage(Math.max(1, currentPage - 1))
                      }
                      disabled={currentPage === 1}
                    />
                    {renderPagination()}
                    <Pagination.Next
                      onClick={() =>
                        setCurrentPage(Math.min(totalPages, currentPage + 1))
                      }
                      disabled={currentPage === totalPages}
                    />
                  </Pagination>
                )}
              </div>
            </>
          )}
        </Card.Body>
      </Card>

      {/* ================= TICKET DETAILS MODAL ================= */}
      <Modal
        show={showView}
        onHide={() => setShowView(false)}
        centered
        size="lg"
        fullscreen="md-down"
      >
        <Modal.Header closeButton>
        <Modal.Title>
          <Chat className="me-2" />
            {formatTicketCode(getTicketReceivedAt(selectedTicket))}
        </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {messagesLoading && messages.length === 0 ? (
            <div className="text-center py-4">
              <Spinner animation="border" />
              <p className="mt-2">Loading conversation...</p>
            </div>
          ) : (
            <>
              {/* TICKET INFO */}
              <Card className="mb-4 border-primary">
                <Card.Body>
                  <Row>
                    <Col md={6}>
                      <h6>
                        <Person className="me-2" />
                        User Information
                      </h6>
                      <p className="mb-1">
                        <strong>Name:</strong> {selectedTicket?.name}
                      </p>
                      <p className="mb-1">
                        <strong>Email:</strong> {selectedTicket?.email || "No email"}
                      </p>
                      <p className="mb-1">
                        <strong>Phone:</strong> {selectedTicket?.number || "No phone number"}
                      </p>
                      <p className="mb-0">
                        <strong>User ID:</strong>{" "}
                        {selectedTicket?.user?._id?.substring(0, 8)}
                      </p>
                    </Col>
                    <Col md={6}>
                      <h6>
                        <Briefcase className="me-2" />
                        Ticket Details
                      </h6>
                      <p className="mb-1">
                        <strong>Subject:</strong> {selectedTicket?.subject}
                      </p>
                      <p className="mb-1">
                        <strong>Project:</strong>{" "}
                        {selectedTicket?.project?.name || "No project"}
                      </p>
                      <p className="mb-1">
                        <strong>Location:</strong>{" "}
                        {selectedTicket?.project?.projectLocation || "-"}
                      </p>
                      {/* <p className="mb-0">
                        <Calendar className="me-2" />
                        <strong>Created:</strong>{" "}
                        {formatDate(selectedTicket?.createdAt)}
                      </p> */}
                    </Col>
                  </Row>
                </Card.Body>
              </Card>

              {/* CONVERSATION */}
              <h6 className="mb-3">
                <Chat className="me-2" />
                Conversation
                <Badge bg="light" text="dark" className="ms-2">
                  {messages.length} messages
                </Badge>
              </h6>

              <div
                className="mb-4 border rounded p-3 bg-light"
                style={{ height: "300px", overflowY: "auto" }}
              >
                {/* LOAD OLDER BUTTON (TOP) */}
                {hasMoreMessages && (
                  <div className="text-center mb-3">
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() =>
                        fetchTicketMessages(
                          selectedTicket._id,
                          messagesPage + 1
                        )
                      }
                      disabled={messagesLoading}
                    >
                      {messagesLoading ? (
                        <Spinner size="sm" />
                      ) : (
                        "See older messages"
                      )}
                    </Button>
                  </div>
                )}

                {/* MESSAGES */}
                {messages.length === 0 ? (
                  <div className="text-center py-5 text-muted">
                    <Chat size={32} />
                    <p className="mt-2">No messages yet</p>
                  </div>
                ) : (
                  messages.map((message, index) => (
                    <div key={index}>{renderMessageBubble(message)}</div>
                  ))
                )}
              </div>

              {/* REPLY FORM */}
              <Form onSubmit={handleSendReply}>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-medium">
                    <Reply className="me-2" />
                    Reply to User
                  </Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    placeholder="Type your response here..."
                    value={replyMessage}
                    onChange={(e) => setReplyMessage(e.target.value)}
                    disabled={replying}
                  />
                </Form.Group>
                <div className="d-flex gap-2 justify-content-end">
                  <Button
                    variant="secondary"
                    onClick={() => setShowView(false)}
                    disabled={replying}
                  >
                    Close
                  </Button>
                  <Button
                    type="submit"
                    variant="primary"
                    disabled={replying || !replyMessage.trim()}
                  >
                    {replying ? (
                      <>
                        <Spinner
                          size="sm"
                          animation="border"
                          className="me-2"
                        />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Reply className="me-2" />
                        Send Reply
                      </>
                    )}
                  </Button>
                </div>
              </Form>
            </>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default HelpAndSupport;
